import React, { useCallback, useMemo, useState } from 'react';
import { Button, Modal, Table } from '@peoplegrove/pg-components';

import moment from 'moment';
import { useConversationFiltersContext } from '../../context/ConversationFiltersContext';
import useFetchConversations from '../../hooks/useFetchConversations';
import ConversationDetails from '../ConversationDetails';
import ConversationsFilters from './ConversationsFilters';
import useDownloadSupportChatCSVMutation from '../../queries/useDownloadSupportChatCSVMutation';
import Rating from '../../components/Rating';
import CsatScore from '../../components/CsatScore/CsatScore';
import classes from './Conversations.module.scss';

const Conversations = ({ setSelectedSessionAddress }) => {
  const { conversationFilters, updatePage, updateLimit } =
    useConversationFiltersContext();

  const { data: conversationsData, isLoading, error } = useFetchConversations();
  const { mutate: downloadCSV, isLoading: isDownloading } =
    useDownloadSupportChatCSVMutation();

  const handleTableChange = useCallback(
    pagination => {
      if (pagination.current !== conversationFilters.page) {
        updatePage(pagination.current);
      }

      if (pagination.pageSize !== conversationFilters.limit) {
        updateLimit(pagination.pageSize);
      }
    },
    [
      conversationFilters.page,
      conversationFilters.limit,
      updatePage,
      updateLimit,
    ]
  );

  const handleDownloadCSV = useCallback(() => {
    downloadCSV(conversationFilters);
  }, [downloadCSV, conversationFilters]);

  const columns = useMemo(
    () => [
      {
        title: 'Username',
        dataIndex: 'username',
        key: 'username',
        ellipsis: true,
        render: text => <span title={text || ''}>{text || ''}</span>,
      },
      {
        title: 'Hub id',
        dataIndex: 'hub_id',
        key: 'hub_id',
        ellipsis: true,
        render: text => <span title={String(text)}>{String(text)}</span>,
      },
      {
        title: 'Hub name',
        dataIndex: 'hub_name',
        key: 'hub_name',
        ellipsis: true,
        render: text => <span title={text || ''}>{text || ''}</span>,
      },
      {
        title: 'Session date',
        dataIndex: 'started_at',
        key: 'started_at',
        ellipsis: true,
        render: date => {
          const formattedDate = moment(date).format('MM/DD/YYYY');
          return <span title={formattedDate}>{formattedDate}</span>;
        },
      },
      {
        title: 'Rating',
        dataIndex: 'rating',
        key: 'rating',
        ellipsis: true,
        render: rating => <Rating rating={rating} />,
      },
      {
        title: 'CSAT Score',
        dataIndex: 'csatScore',
        key: 'csatScore',
        ellipsis: true,
        render: score => <CsatScore score={score} />,
      },
      {
        title: 'Zendesk Ticket',
        dataIndex: 'tickets',
        key: 'tickets',
        ellipsis: true,
        render: tickets => (tickets.length > 0 ? 'Yes' : 'No'),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 140,
        render: (_, record) => (
          <Button
            type="link"
            size="small"
            onClick={() => setSelectedSessionAddress(record.address)}
            aria-label={`View details for conversation with ${record.username}`}
          >
            View Details
          </Button>
        ),
      },
    ],
    []
  );

  if (error) {
    return (
      <div className="ck-card">
        <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
          <p>Error loading conversations. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ck-card">
      <div className={classes.conversationHeader}>
        <h2>Conversations</h2>
        <Button
          type="secondary"
          size="small"
          icon="pg-download-o"
          onClick={handleDownloadCSV}
          disabled={isDownloading || conversationsData?.conversations?.length === 0}
          loading={isDownloading}
        >
          {isDownloading ? 'Downloading...' : 'Export to CSV'}
        </Button>
      </div>
      <ConversationsFilters />
      <div>
        <Table
          columns={columns}
          dataSource={conversationsData?.conversations || []}
          rowKey="address"
          loading={isLoading}
          caption="Support widget conversations"
          aria-label="Conversations list"
          pagination={{
            current: conversationFilters.page || 1,
            pageSize: conversationFilters.limit,
            total: conversationsData?.totalCount || 0,
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  );
};

export default Conversations;
